import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SINGULAR_EVENTS } from '../constants/singularEventTypes';
import {
  selectPathCountry,
  selectSectionCollection,
} from '../containers/AppSectionRouter/selectors';
import {
  selectAccessToken,
  selectAuthenticated,
} from '../containers/App/selectors';
import useClientConfigs from './useClientConfigs';
import { getSubscriberIdFromToken } from '../utils/jwtUtils';
import {
  initSingular,
  isSingularEnvEnabled,
  isSingularEventEnabled,
  getSingularEnvCredentials,
  resolveSectionForPathname,
  setSingularSubscriberId,
  trackScreenView,
  trackSingularPageVisit,
} from '../utils/singularUtils';

/**
 * Loads/inits Singular when TBX_SINGULAR_ENABLED=true and credentials exist.
 * If the flag/vars are missing, Singular stays off and the app continues normally.
 * screen_view follows `useSingular.events` in client config. Uses pageVisit() for SPA
 * navigations after the first load (init already reports the initial page visit).
 */
export default function useSingularTracking() {
  const location = useLocation();
  const isFirstVisit = React.useRef(true);
  const { useSingular } = useClientConfigs();
  const sectionCollection = useSelector((state) =>
    selectSectionCollection(state),
  );
  const pathCountry = useSelector((state) => selectPathCountry(state));
  const authenticated = useSelector((state) => selectAuthenticated(state));
  const accessToken = useSelector((state) => selectAccessToken(state));
  const enabled = isSingularEnvEnabled();
  const screenViewEnabled = isSingularEventEnabled(
    SINGULAR_EVENTS.SCREEN_VIEW,
    useSingular?.events,
  );
  const credentials = getSingularEnvCredentials();

  React.useEffect(() => {
    setSingularSubscriberId(
      authenticated
        ? getSubscriberIdFromToken(accessToken?.access_token)
        : null,
    );
  }, [authenticated, accessToken]);

  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const { sdkKey, sdkSecret, bundleId } = credentials;
    let cancelled = false;

    (async () => {
      try {
        const ready = await initSingular({ sdkKey, sdkSecret, bundleId });

        if (cancelled || !ready) {
          return;
        }

        if (isFirstVisit.current) {
          isFirstVisit.current = false;
        } else if (screenViewEnabled) {
          trackSingularPageVisit();
        }

        if (screenViewEnabled) {
          const section = resolveSectionForPathname(
            location.pathname,
            sectionCollection,
            pathCountry,
          );

          trackScreenView({
            screen_name: location.pathname,
            page_url: window.location.href,
            page_title: document.title,
            slug: location.pathname,
            section_name: section?.name ?? null,
          });
        }
      } catch (error) {
        console.warn('[Singular] tracking failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    enabled,
    screenViewEnabled,
    sectionCollection,
    pathCountry,
    credentials.sdkKey,
    credentials.sdkSecret,
    credentials.bundleId,
  ]);
}
